import { computed, observable, runInAction } from 'mobx'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { task } from 'mobx-task'
import { isAbortError, resolveOnAbort } from '@/utils/abort-util'
import { computeProgressionFlow } from '@/features/jobs/taxes/progression/progression-content'
import type { ActivationLifecycle } from '@/infra/activation'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { ProgressionStore } from '@/features/jobs/taxes/progression/ProgressionStore'
import type { JobProgression } from '@/features/jobs/taxes/progression/JobProgression'
import type {
  SnackbarErrorHandlerFactory,
  SnackbarRetryWrapperFactory,
} from '@/features/shell'
import { DraftReviewViewModel } from '@/features/jobs/details/tax-type/DraftReviewViewModel'
import { ExtensionViewModel } from '@/features/jobs/details/tax-type/ExtensionViewModel'
import type { DocumentsListViewModelFactory } from '@/features/docs'
import { FinalReturnViewModel } from '@/features/jobs/details/tax-type/FinalReturnViewModel'

/**
 * Function that returns the current step.
 */
export type GetProgressionFn = () => JobProgression | null

/**
 * Manages the job progression state.
 */
export class ProgressionViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * View model for managing the draft review.
   */
  readonly draftReviewVm

  /**
   * View model for managing extension-related functionality.
   */
  readonly extensionVm

  /**
   * View model for managing the final return document.
   */
  readonly finalReturnVm

  /**
   * The loaded progression.
   */
  @observable accessor progression: JobProgression | null = null

  constructor(
    public readonly job: SubmittedJob,
    private readonly _createDocumentsListViewModel: DocumentsListViewModelFactory,
    private readonly _progressionStore: ProgressionStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
  ) {
    this.draftReviewVm = new DraftReviewViewModel(
      this.job,
      () => this.progression,
      this._createDocumentsListViewModel,
      this._progressionStore,
      this._createSnackbarErrorHandler,
    )
    this.extensionVm = new ExtensionViewModel(
      this.job,
      () => this.progression,
      this._progressionStore,
      this._createSnackbarErrorHandler,
    )
    this.finalReturnVm = new FinalReturnViewModel(
      this.job,
      () => this.progression,
      this._createDocumentsListViewModel,
    )
    this._lifecycle.manage(this.draftReviewVm)
    this._lifecycle.manage(this.extensionVm)
    this._lifecycle.manage(this.finalReturnVm)
  }

  /**
   * The current step in the progression, if any.
   */
  @computed
  get currentStep() {
    return this.progression?.current ?? null
  }

  /**
   * Content for the current progression step.
   */
  @computed
  get currentStepContent() {
    // If the job is an extension flow, delegate to the extension
    // view model to get the extension-related overrides for the
    // content of the steps.
    if (this.extensionVm.isExtensionFlow) {
      return this.extensionVm.currentStepContent
    }

    const step = this.allSteps.find((s) => s.step === this.currentStep)
    return step?.content ?? null
  }

  /**
   * All steps in the current progression flow.
   */
  @computed
  get allSteps() {
    // If the job is an extension flow, delegate to the extension
    // view model to get the extension-related overrides for the
    // content of the steps.
    if (this.extensionVm.isExtensionFlow) {
      return this.extensionVm.allSteps
    }

    const recordedSteps =
      this.progression?.recordedSteps.items.map((s) => s.step) ?? []

    return computeProgressionFlow({
      flow: this.progression?.flow,
      steps: recordedSteps,
      isExtensionOnlyJob: this.extensionVm.isExtensionOnlyJob,
    })
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._lifecycle.activate()
    const signal = this._lifecycle.signal
    void this.fetchProgression(signal)
  }

  /**
   * Fetches the progression.
   */
  fetchProgression = task(async (signal: AbortSignal) => {
    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to load job progression',
      signal,
    )

    const progression = await withRetry(() =>
      resolveOnAbort(
        this._progressionStore.fetchProgression(this.job.id),
        signal,
      ),
    )

    if (isAbortError(progression)) {
      return
    }

    runInAction(() => (this.progression = progression))
  })

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
